import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import {
  XMarkIcon,
  PhotoIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  PlusIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/outline";
import Spinner from "../../ui/Spinner";
import { MAX_NOTE_IMAGES } from "../../utils/constants";

const VP_WIDTH = 300;
const VP_HEIGHT = 400; // 3:4 portrait ratio
const CROP_OUTPUT_WIDTH = 1080;
const CROP_OUTPUT_HEIGHT = 1440;
const MAX_ZOOM = 5;
const ELASTIC_RESISTANCE = 0.35;
const SNAP_BACK_MS = 300;
const CROP_BORDER_RADIUS = 12;

function clamp(val, min, max) {
  return Math.min(Math.max(val, min), max);
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

function fileKey(file) {
  return `${file.name}|${file.size}|${file.lastModified}`;
}

function computeContainFit(nW, nH, rot) {
  const isSwapped = rot === 90 || rot === 270;
  const effW = isSwapped ? nH : nW;
  const effH = isSwapped ? nW : nH;
  const cScale = Math.min(VP_WIDTH / effW, VP_HEIGHT / effH);
  const containW = effW * cScale;
  const containH = effH * cScale;
  const cssW = isSwapped ? containH : containW;
  const cssH = isSwapped ? containW : containH;
  const minZoom = Math.max(VP_WIDTH / containW, VP_HEIGHT / containH);
  return { containScale: cScale, containW, containH, cssW, cssH, minZoom };
}

export default function ImageCropModal({
  initialFiles,
  onSave,
  onClose,
  existingImageCount = 0,
  existingImagePreviews = [], // preview URLs for already-saved images
  onRemoveExisting, // (index: number) => void
}) {
  // ---- Photo list ----
  const [photos, setPhotos] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);

  // ---- Live crop state ----
  const [zoomFactor, setZoomFactor] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [rotation, setRotation] = useState(0); // 0, 90, 180, 270
  const [imgLoaded, setImgLoaded] = useState(false);
  const [isSnapping, setIsSnapping] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // ---- Refs ----
  const cropStatesRef = useRef([]); // Array<{ zoomFactor, offset, rotation } | null>
  const imgRef = useRef(null);
  const viewportRef = useRef(null);
  const containScaleRef = useRef(1);
  const containWRef = useRef(0);
  const containHRef = useRef(0);
  const cssWRef = useRef(0);
  const cssHRef = useRef(0);
  const minZoomRef = useRef(1);
  const handleWheelRef = useRef(null);
  const dragStartRef = useRef(null);
  const pinchRef = useRef(null);
  const activePointersRef = useRef(new Map());
  const fileInputRef = useRef(null);
  const hasRestoredStateRef = useRef(false);
  const prevIndexRef = useRef(null);
  const removalTransitionRef = useRef(false);
  const currentRotationRef = useRef(0);
  const photosRef = useRef([]);

  // Keep refs in sync
  useEffect(() => {
    currentRotationRef.current = rotation;
  }, [rotation]);
  useEffect(() => {
    photosRef.current = photos;
  }, [photos]);

  // Cleanup all object URLs on unmount
  useEffect(() => {
    return () => {
      photosRef.current.forEach((p) => URL.revokeObjectURL(p.objectUrl));
    };
  }, []);

  // ---- Initialize photos from initialFiles (deduplicate) ----
  useEffect(() => {
    const seen = new Set();
    const unique = [];
    for (const file of initialFiles) {
      const key = fileKey(file);
      if (!seen.has(key)) {
        seen.add(key);
        unique.push(file);
      }
    }
    const entries = unique.map((file) => ({
      file,
      objectUrl: URL.createObjectURL(file),
    }));
    setPhotos(entries);
    cropStatesRef.current = new Array(entries.length).fill(null);
    prevIndexRef.current = 0;
    setActiveIndex(0);
  }, []);

  // ---- Crop state save/restore ----
  const saveCropState = useCallback(
    (index) => {
      if (index >= 0 && index < cropStatesRef.current.length) {
        cropStatesRef.current[index] = {
          zoomFactor,
          offset: { ...offset },
          rotation,
        };
      }
    },
    [zoomFactor, offset, rotation],
  );
  const saveCropStateRef = useRef(saveCropState);
  saveCropStateRef.current = saveCropState;

  // Navigation: save outgoing, restore incoming
  useEffect(() => {
    // Skip when handleRemovePhoto already handled this transition
    if (removalTransitionRef.current) {
      removalTransitionRef.current = false;
      prevIndexRef.current = activeIndex;
      return;
    }

    if (prevIndexRef.current !== null && prevIndexRef.current !== activeIndex) {
      saveCropStateRef.current(prevIndexRef.current);
    }
    prevIndexRef.current = activeIndex;

    const saved = cropStatesRef.current[activeIndex];
    if (saved) {
      setZoomFactor(saved.zoomFactor);
      setOffset(saved.offset);
      setRotation(saved.rotation);
      currentRotationRef.current = saved.rotation;
      hasRestoredStateRef.current = true;
    } else {
      setZoomFactor(1);
      setOffset({ x: 0, y: 0 });
      setRotation(0);
      currentRotationRef.current = 0;
      hasRestoredStateRef.current = false;
    }
    setImgLoaded(false);
    setIsSnapping(false);
  }, [activeIndex]);

  // ---- Physics functions ----
  function clampOff(off, zf) {
    const maxX = Math.max(0, (containWRef.current * zf - VP_WIDTH) / 2);
    const maxY = Math.max(0, (containHRef.current * zf - VP_HEIGHT) / 2);
    return {
      x: clamp(off.x, -maxX, maxX),
      y: clamp(off.y, -maxY, maxY),
    };
  }

  function elasticClamp(off, zf) {
    const maxX = Math.max(0, (containWRef.current * zf - VP_WIDTH) / 2);
    const maxY = Math.max(0, (containHRef.current * zf - VP_HEIGHT) / 2);
    return {
      x: elasticAxis(off.x, maxX),
      y: elasticAxis(off.y, maxY),
    };
  }

  function elasticAxis(val, limit) {
    if (val > limit) return limit + (val - limit) * ELASTIC_RESISTANCE;
    if (val < -limit) return -limit + (val + limit) * ELASTIC_RESISTANCE;
    return val;
  }

  function elasticZoom(zf) {
    const minZf = minZoomRef.current;
    if (zf >= minZf) return zf;
    const underscroll = minZf - zf;
    return minZf - underscroll * ELASTIC_RESISTANCE;
  }

  function zoomToward(px, py, newZf, curZf, curOff) {
    const ratio = newZf / curZf;
    return clampOff(
      {
        x: (px - VP_WIDTH / 2) * (1 - ratio) + curOff.x * ratio,
        y: (py - VP_HEIGHT / 2) * (1 - ratio) + curOff.y * ratio,
      },
      newZf,
    );
  }

  // ---- Image load handler ----
  const handleImgLoad = () => {
    const img = imgRef.current;
    if (!img) return;

    const nW = img.naturalWidth;
    const nH = img.naturalHeight;
    const rot = currentRotationRef.current;
    const fit = computeContainFit(nW, nH, rot);

    containScaleRef.current = fit.containScale;
    containWRef.current = fit.containW;
    containHRef.current = fit.containH;
    cssWRef.current = fit.cssW;
    cssHRef.current = fit.cssH;
    minZoomRef.current = fit.minZoom;

    if (!hasRestoredStateRef.current) {
      setZoomFactor(fit.minZoom);
      setOffset({ x: 0, y: 0 });
    }
    hasRestoredStateRef.current = false;
    setImgLoaded(true);
  };

  // ---- Rotate handler ----
  const handleRotate = () => {
    const img = imgRef.current;
    if (!img || !imgLoaded) return;

    const newRot = (rotation + 90) % 360;
    setRotation(newRot);
    currentRotationRef.current = newRot;

    const nW = img.naturalWidth;
    const nH = img.naturalHeight;
    const fit = computeContainFit(nW, nH, newRot);

    containScaleRef.current = fit.containScale;
    containWRef.current = fit.containW;
    containHRef.current = fit.containH;
    cssWRef.current = fit.cssW;
    cssHRef.current = fit.cssH;
    minZoomRef.current = fit.minZoom;

    setZoomFactor(fit.minZoom);
    setOffset({ x: 0, y: 0 });
    setIsSnapping(false);
  };

  // ---- Pointer handlers ----
  const handlePointerDown = (e) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    activePointersRef.current.set(e.pointerId, {
      clientX: e.clientX,
      clientY: e.clientY,
    });

    if (activePointersRef.current.size === 2) {
      if (isSnapping) setIsSnapping(false);
      const points = [...activePointersRef.current.values()];
      const rect = viewportRef.current.getBoundingClientRect();
      pinchRef.current = {
        initialDist: Math.hypot(
          points[1].clientX - points[0].clientX,
          points[1].clientY - points[0].clientY,
        ),
        initialZf: zoomFactor,
        initialOffset: { ...offset },
        midX: (points[0].clientX + points[1].clientX) / 2 - rect.left,
        midY: (points[0].clientY + points[1].clientY) / 2 - rect.top,
      };
      dragStartRef.current = null;
    } else {
      if (isSnapping) setIsSnapping(false);
      dragStartRef.current = {
        clientX: e.clientX,
        clientY: e.clientY,
        offsetX: offset.x,
        offsetY: offset.y,
      };
    }
  };

  const handlePointerMove = (e) => {
    activePointersRef.current.set(e.pointerId, {
      clientX: e.clientX,
      clientY: e.clientY,
    });

    if (pinchRef.current && activePointersRef.current.size === 2) {
      const points = [...activePointersRef.current.values()];
      const currentDist = Math.hypot(
        points[1].clientX - points[0].clientX,
        points[1].clientY - points[0].clientY,
      );
      const { initialDist, initialZf, initialOffset, midX, midY } =
        pinchRef.current;

      const rawZf = initialZf * (currentDist / initialDist);
      const floorZf = minZoomRef.current * 0.7;
      const boundedZf = clamp(rawZf, floorZf, MAX_ZOOM);
      const displayZf =
        boundedZf < minZoomRef.current ? elasticZoom(boundedZf) : boundedZf;

      setZoomFactor(displayZf);

      const ratio = displayZf / initialZf;
      const rawOff = {
        x: (midX - VP_WIDTH / 2) * (1 - ratio) + initialOffset.x * ratio,
        y: (midY - VP_HEIGHT / 2) * (1 - ratio) + initialOffset.y * ratio,
      };
      setOffset(elasticClamp(rawOff, displayZf));
      pinchRef.current.rawZf = rawZf;
    } else if (dragStartRef.current) {
      const dx = e.clientX - dragStartRef.current.clientX;
      const dy = e.clientY - dragStartRef.current.clientY;
      if (isSnapping) setIsSnapping(false);
      setOffset(
        elasticClamp(
          {
            x: dragStartRef.current.offsetX + dx,
            y: dragStartRef.current.offsetY + dy,
          },
          zoomFactor,
        ),
      );
    }
  };

  const handlePointerUp = (e) => {
    activePointersRef.current.delete(e.pointerId);
    if (activePointersRef.current.size < 2) {
      if (pinchRef.current && pinchRef.current.rawZf != null) {
        const minZf = minZoomRef.current;
        if (pinchRef.current.rawZf < minZf) {
          setIsSnapping(true);
          setZoomFactor(minZf);
          setOffset((prev) => clampOff(prev, minZf));
        }
      }
      pinchRef.current = null;
    }
    if (activePointersRef.current.size === 0) {
      setIsSnapping(true);
      setOffset((prev) => clampOff(prev, zoomFactor));
      dragStartRef.current = null;
    }
  };

  // Wheel zoom (non-passive)
  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const handler = (e) => {
      e.preventDefault();
      if (handleWheelRef.current) handleWheelRef.current(e);
    };
    el.addEventListener("wheel", handler, { passive: false });
    return () => el.removeEventListener("wheel", handler);
  }, []);

  useEffect(() => {
    handleWheelRef.current = (e) => {
      if (!imgLoaded) return;
      const direction = e.deltaY > 0 ? -1 : 1;
      const newZf = clamp(
        zoomFactor * (1 + direction * 0.1),
        minZoomRef.current,
        MAX_ZOOM,
      );
      const rect = viewportRef.current.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;
      setZoomFactor(newZf);
      setOffset(zoomToward(mouseX, mouseY, newZf, zoomFactor, offset));
    };
  });

  // ---- Remove a photo by index ----
  const handleRemovePhoto = (index) => {
    URL.revokeObjectURL(photos[index].objectUrl);

    const newPhotos = photos.filter((_, i) => i !== index);
    cropStatesRef.current = cropStatesRef.current.filter((_, i) => i !== index);

    if (newPhotos.length === 0) {
      if (existingImagePreviews.length === 0) {
        onClose();
        return;
      }
      // Stay open — user can still manage existing images or add more
      setPhotos([]);
      cropStatesRef.current = [];
      setActiveIndex(0);
      return;
    }

    // Determine new active index
    let newIndex;
    if (index < activeIndex) {
      newIndex = activeIndex - 1;
    } else if (index === activeIndex) {
      newIndex = Math.min(index, newPhotos.length - 1);
    } else {
      newIndex = activeIndex;
    }

    // Only restore crop state when the displayed photo actually changes
    if (index === activeIndex) {
      const saved = cropStatesRef.current[newIndex];
      if (saved) {
        setZoomFactor(saved.zoomFactor);
        setOffset(saved.offset);
        setRotation(saved.rotation);
        currentRotationRef.current = saved.rotation;
        hasRestoredStateRef.current = true;
      } else {
        setZoomFactor(1);
        setOffset({ x: 0, y: 0 });
        setRotation(0);
        currentRotationRef.current = 0;
        hasRestoredStateRef.current = false;
      }
      setImgLoaded(false);
    }

    // If activeIndex value changes due to removal, skip the navigation useEffect
    // to avoid a race condition where the useEffect resets imgLoaded after the
    // new image has already loaded from cache
    if (newIndex !== activeIndex) {
      removalTransitionRef.current = true;
    }

    prevIndexRef.current = newIndex;
    setPhotos(newPhotos);
    setActiveIndex(newIndex);
  };

  // ---- Add more photos (deduplicate) ----
  const handleAddMore = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    const existingKeys = new Set(photos.map((p) => fileKey(p.file)));
    const unique = files.filter((f) => {
      const key = fileKey(f);
      if (existingKeys.has(key)) return false;
      existingKeys.add(key);
      return true;
    });

    const remaining = MAX_NOTE_IMAGES - existingImageCount - photos.length;
    const filesToAdd = unique.slice(0, Math.max(0, remaining));
    if (!filesToAdd.length) return;

    const newEntries = filesToAdd.map((file) => ({
      file,
      objectUrl: URL.createObjectURL(file),
    }));

    setPhotos((prev) => [...prev, ...newEntries]);
    cropStatesRef.current = [
      ...cropStatesRef.current,
      ...new Array(newEntries.length).fill(null),
    ];
    e.target.value = null;
  };

  // ---- Save all ----
  const handleSaveAll = async () => {
    setIsSaving(true);
    saveCropStateRef.current(activeIndex);

    const blobs = [];

    for (let i = 0; i < photos.length; i++) {
      const photo = photos[i];
      const saved = cropStatesRef.current[i];

      try {
        const img = await loadImage(photo.objectUrl);
        const nW = img.naturalWidth;
        const nH = img.naturalHeight;
        const rot = saved ? saved.rotation : 0;
        const isSwapped = rot === 90 || rot === 270;
        const effW = isSwapped ? nH : nW;
        const effH = isSwapped ? nW : nH;
        const cScale = Math.min(VP_WIDTH / effW, VP_HEIGHT / effH);
        const minZoom = Math.max(
          VP_WIDTH / (effW * cScale),
          VP_HEIGHT / (effH * cScale),
        );

        const zf = saved ? saved.zoomFactor : minZoom;
        const off = saved ? saved.offset : { x: 0, y: 0 };
        const scale = cScale * zf;

        // Crop rectangle in rotated image space
        const sx = effW / 2 - (VP_WIDTH / 2 + off.x) / scale;
        const sy = effH / 2 - (VP_HEIGHT / 2 + off.y) / scale;
        const sw = VP_WIDTH / scale;
        const sh = VP_HEIGHT / scale;

        const canvas = document.createElement("canvas");
        canvas.width = CROP_OUTPUT_WIDTH;
        canvas.height = CROP_OUTPUT_HEIGHT;
        const ctx = canvas.getContext("2d");

        if (rot === 0) {
          ctx.drawImage(
            img,
            sx,
            sy,
            sw,
            sh,
            0,
            0,
            CROP_OUTPUT_WIDTH,
            CROP_OUTPUT_HEIGHT,
          );
        } else {
          ctx.save();
          // Map output canvas to crop region, then apply forward rotation
          ctx.scale(CROP_OUTPUT_WIDTH / sw, CROP_OUTPUT_HEIGHT / sh);
          ctx.translate(-sx, -sy);
          ctx.translate(effW / 2, effH / 2);
          ctx.rotate((rot * Math.PI) / 180);
          ctx.translate(-nW / 2, -nH / 2);
          ctx.drawImage(img, 0, 0);
          ctx.restore();
        }

        const blob = await new Promise((resolve) => {
          canvas.toBlob((b) => resolve(b), "image/jpeg", 0.9);
        });
        blobs.push(blob);
      } catch {
        // Skip failed images
      }
    }

    onSave(blobs);
    setIsSaving(false);
  };

  // ---- Cancel ----
  const handleCancel = () => {
    onClose();
  };

  // ---- Derived state ----
  const currentPhoto = photos[activeIndex];
  const canPrev = activeIndex > 0;
  const canNext = activeIndex < photos.length - 1;
  const canAddMore = existingImageCount + photos.length < MAX_NOTE_IMAGES;

  const modal = (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/40"
        aria-hidden="true"
        onClick={isSaving ? undefined : handleCancel}
      />
      <div className="relative w-full max-w-md max-h-[90vh] rounded-2xl bg-surface-elevated border border-border shadow-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <header className="flex items-center justify-between px-5 py-3 border-b border-border bg-surface">
          <div className="flex items-center gap-2 text-text-primary">
            <PhotoIcon className="h-5 w-5 text-primary" />
            <h3 className="text-sm font-semibold">
              {photos.length > 0 ? "Crop photos" : "Manage photos"}
              {photos.length > 1 && (
                <span className="ml-1.5 text-text-secondary font-normal">
                  ({activeIndex + 1} of {photos.length})
                </span>
              )}
            </h3>
          </div>
          <button
            onClick={handleCancel}
            disabled={isSaving}
            className="p-1 rounded hover:bg-surface-sunken disabled:opacity-50"
            aria-label="Close"
          >
            <XMarkIcon className="h-5 w-5 text-text-secondary" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto pretty-scroll p-5 space-y-4">
          {photos.length > 0 ? (
            <>
              <p className="text-[13px] text-text-secondary text-center">
                Drag to pan · Scroll or pinch to zoom
              </p>

              {/* Crop viewport with navigation arrows */}
              <div className="flex items-center justify-center gap-2">
                <button
                  onClick={() => setActiveIndex((i) => i - 1)}
                  disabled={!canPrev || isSaving}
                  className="p-1.5 rounded-full hover:bg-surface-sunken disabled:opacity-30 disabled:cursor-default"
                  aria-label="Previous photo"
                >
                  <ChevronLeftIcon className="h-5 w-5 text-text-secondary" />
                </button>

                <div
                  ref={viewportRef}
                  className="relative overflow-hidden rounded-lg bg-black flex-shrink-0"
                  style={{
                    width: VP_WIDTH,
                    height: VP_HEIGHT,
                    touchAction: "none",
                  }}
                  onPointerDown={isSaving ? undefined : handlePointerDown}
                  onPointerMove={isSaving ? undefined : handlePointerMove}
                  onPointerUp={isSaving ? undefined : handlePointerUp}
                  onPointerCancel={isSaving ? undefined : handlePointerUp}
                >
                  {currentPhoto && (
                    <img
                      ref={imgRef}
                      src={currentPhoto.objectUrl}
                      alt="Crop preview"
                      draggable={false}
                      onLoad={handleImgLoad}
                      className="absolute pointer-events-none select-none max-w-none max-h-none"
                      style={
                        imgLoaded
                          ? {
                              width: cssWRef.current,
                              height: cssHRef.current,
                              left: (VP_WIDTH - cssWRef.current) / 2,
                              top: (VP_HEIGHT - cssHRef.current) / 2,
                              transform: `translate3d(${offset.x}px, ${offset.y}px, 0px) scale(${zoomFactor}) rotate(${rotation}deg)`,
                              transformOrigin: "center center",
                              willChange: "transform",
                              transition: isSnapping
                                ? `transform ${SNAP_BACK_MS}ms cubic-bezier(0.25, 0.46, 0.45, 0.94)`
                                : "none",
                            }
                          : { visibility: "hidden" }
                      }
                      onTransitionEnd={() => setIsSnapping(false)}
                    />
                  )}

                  {imgLoaded && (
                    <svg
                      className="absolute inset-0 pointer-events-none"
                      width={VP_WIDTH}
                      height={VP_HEIGHT}
                      shapeRendering="geometricPrecision"
                    >
                      <defs>
                        <mask id="imageCropMask">
                          <rect width={VP_WIDTH} height={VP_HEIGHT} fill="white" />
                          <rect
                            x={0}
                            y={0}
                            width={VP_WIDTH}
                            height={VP_HEIGHT}
                            rx={CROP_BORDER_RADIUS}
                            ry={CROP_BORDER_RADIUS}
                            fill="black"
                          />
                        </mask>
                      </defs>
                      <rect
                        width={VP_WIDTH}
                        height={VP_HEIGHT}
                        fill="rgba(0,0,0,0.55)"
                        mask="url(#imageCropMask)"
                      />
                      <rect
                        x={0}
                        y={0}
                        width={VP_WIDTH}
                        height={VP_HEIGHT}
                        rx={CROP_BORDER_RADIUS}
                        ry={CROP_BORDER_RADIUS}
                        fill="none"
                        stroke="rgba(255,255,255,0.7)"
                        strokeWidth="1.5"
                        vectorEffect="non-scaling-stroke"
                      />
                    </svg>
                  )}

                  {!imgLoaded && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Spinner size={28} className="text-white" />
                    </div>
                  )}
                </div>

                <button
                  onClick={() => setActiveIndex((i) => i + 1)}
                  disabled={!canNext || isSaving}
                  className="p-1.5 rounded-full hover:bg-surface-sunken disabled:opacity-30 disabled:cursor-default"
                  aria-label="Next photo"
                >
                  <ChevronRightIcon className="h-5 w-5 text-text-secondary" />
                </button>
              </div>

              {/* Action buttons: Rotate, Add more */}
              <div className="flex items-center justify-center gap-4">
                <button
                  onClick={handleRotate}
                  disabled={isSaving || !imgLoaded}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-text-secondary text-sm [@media(hover:hover)]:hover:bg-surface-sunken disabled:opacity-50"
                  title="Rotate 90°"
                >
                  <ArrowPathIcon className="h-4 w-4" />
                  Rotate
                </button>

                <button
                  onClick={() => fileInputRef.current.click()}
                  disabled={isSaving || !canAddMore}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-text-secondary text-sm [@media(hover:hover)]:hover:bg-surface-sunken disabled:opacity-50"
                  title="Add more photos"
                >
                  <PlusIcon className="h-4 w-4" />
                  Add more
                </button>
              </div>
            </>
          ) : (
            <>
              {/* Empty state — no batch photos to crop */}
              <div className="flex justify-center">
                <div
                  className="relative overflow-hidden rounded-lg bg-black/30 flex-shrink-0 flex flex-col items-center justify-center gap-3 cursor-pointer"
                  style={{ width: VP_WIDTH, height: VP_HEIGHT }}
                  onClick={() => canAddMore && fileInputRef.current.click()}
                >
                  <PlusIcon className="h-10 w-10 text-white/40" />
                  <span className="text-sm text-white/50">Add photos</span>
                </div>
              </div>

              {/* Add photos button */}
              <div className="flex items-center justify-center">
                <button
                  onClick={() => fileInputRef.current.click()}
                  disabled={isSaving || !canAddMore}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-text-secondary text-sm [@media(hover:hover)]:hover:bg-surface-sunken disabled:opacity-50"
                  title="Add photos"
                >
                  <PlusIcon className="h-4 w-4" />
                  Add photos
                </button>
              </div>
            </>
          )}

          {/* Thumbnail strip: saved images + current batch (5 per row) */}
          {(existingImagePreviews.length > 0 || photos.length > 0) && (
            <div className="flex justify-center">
              <div className="grid grid-cols-5 gap-2">
                {/* Previously saved images */}
                {existingImagePreviews.map((src, i) => (
                  <div
                    key={`saved-${i}`}
                    className="relative w-10 h-[53px] rounded-md overflow-visible"
                  >
                    <img
                      src={src}
                      alt=""
                      className="w-full h-full object-cover rounded-md opacity-50 ring-1 ring-border pointer-events-none"
                    />
                    {onRemoveExisting && (
                      <button
                        type="button"
                        onClick={() => onRemoveExisting(i)}
                        disabled={isSaving}
                        className="absolute -top-1.5 -right-1.5 bg-surface-elevated rounded-full border border-border-strong text-text-destructive px-1 text-xs shadow [@media(hover:hover)]:hover:bg-destructive-50 disabled:opacity-50"
                        title="Remove image"
                      >
                        ⛔
                      </button>
                    )}
                  </div>
                ))}
                {/* Current batch thumbnails */}
                {photos.map((photo, i) => (
                  <div
                    key={`batch-${i}`}
                    className="relative w-10 h-[53px] overflow-visible"
                  >
                    <button
                      type="button"
                      onClick={() => setActiveIndex(i)}
                      disabled={isSaving}
                      className={`w-full h-full rounded-md overflow-hidden flex-shrink-0 transition-all ${
                        i === activeIndex
                          ? "ring-2 ring-primary ring-offset-1 ring-offset-surface-elevated"
                          : "ring-1 ring-border opacity-70 [@media(hover:hover)]:hover:opacity-100"
                      }`}
                    >
                      <img
                        src={photo.objectUrl}
                        alt=""
                        className="w-full h-full object-cover pointer-events-none"
                      />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleRemovePhoto(i)}
                      disabled={isSaving}
                      className="absolute -top-1.5 -right-1.5 bg-surface-elevated rounded-full border border-border-strong text-text-destructive px-1 text-xs shadow [@media(hover:hover)]:hover:bg-destructive-50 disabled:opacity-50"
                      title="Remove image"
                    >
                      ⛔
                    </button>
                  </div>
                ))}
                {/* Empty slot indicators */}
                {Array.from(
                  { length: MAX_NOTE_IMAGES - existingImageCount - photos.length },
                  (_, i) => (
                    <div
                      key={`empty-${i}`}
                      className="w-10 h-[53px] rounded-md border border-dashed border-border-strong"
                    />
                  ),
                )}
              </div>
            </div>
          )}

          <input
            type="file"
            accept="image/*"
            multiple
            ref={fileInputRef}
            onChange={handleAddMore}
            className="hidden"
          />

        </div>

        {/* Footer - Fixed at bottom */}
        <div className="flex items-center justify-between px-5 py-3 border-t border-border">
          <button
            className="px-3 py-1.5 rounded-md bg-surface-sunken text-text-secondary border border-border-strong text-sm hover:bg-surface disabled:opacity-50"
            onClick={handleCancel}
            disabled={isSaving}
          >
            Cancel
          </button>
          <button
            className="px-4 py-1.5 rounded-md bg-primary hover:bg-primary-hover text-text-inverse text-sm disabled:opacity-60 flex items-center gap-2"
            onClick={photos.length > 0 ? handleSaveAll : handleCancel}
            disabled={isSaving}
          >
            {isSaving && <Spinner size={14} className="text-white" />}
            {isSaving ? "Saving..." : photos.length > 0 ? "Save" : "Done"}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
